/**
 * Botão "Esvaziar" do carrinho (gaveta).
 *
 * Mostra uma confirmação no visual do site (dentro da própria gaveta),
 * limpa o carrinho pelo recurso padrão da Shopify (/cart/clear.js) e
 * atualiza a gaveta e a página /cart na hora, do mesmo jeito que o tema
 * faz quando a quantidade muda: pede as seções do carrinho junto com a
 * limpeza e troca o HTML delas.
 *
 * Não mexe em preço, desconto nem checkout.
 */
import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';

async function esvaziar() {
  const secoes = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  const raiz = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const resposta = await fetch(`${raiz}cart/clear.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ sections: secoes.join(','), sections_url: window.location.pathname }),
  });
  const dados = await resposta.json();
  const html = dados.sections || {};

  secoes.forEach((id) => {
    if (html[id]) morphSection(id, html[id]);
  });

  document.dispatchEvent(
    new CartUpdateEvent({}, 'lcw-esvaziar', { itemCount: 0, source: 'lcw-esvaziar', sections: html })
  );
}

/* Confirmação: um cartão sobre a gaveta, com "Cancelar" e "Esvaziar".
   Esc ou clique fora cancela. O foco vai para o "Cancelar" — a opção
   segura — para um Enter sem querer não apagar nada. */
function confirmar(botao) {
  return new Promise((resolve) => {
    const host = botao.closest('dialog') || document.body;
    const camada = document.createElement('div');
    camada.className = 'lcw-confirma';
    camada.innerHTML = `
      <div class="lcw-confirma__cartao" role="alertdialog" aria-modal="true" aria-labelledby="lcw-confirma-titulo" aria-describedby="lcw-confirma-texto">
        <p class="lcw-confirma__titulo" id="lcw-confirma-titulo">Esvaziar o carrinho?</p>
        <p class="lcw-confirma__texto" id="lcw-confirma-texto">Todos os itens serão removidos.</p>
        <div class="lcw-confirma__botoes">
          <button type="button" class="lcw-confirma__btn lcw-confirma__btn--secundario" data-acao="cancelar">Cancelar</button>
          <button type="button" class="lcw-confirma__btn lcw-confirma__btn--primario" data-acao="esvaziar">Esvaziar</button>
        </div>
      </div>`;

    const fechar = (resultado) => {
      document.removeEventListener('keydown', aoTeclar, true);
      if (host.tagName === 'DIALOG') host.removeEventListener('cancel', aoCancelarDialog);
      camada.remove();
      if (!resultado) botao.focus();
      resolve(resultado);
    };
    const aoTeclar = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        fechar(false);
      }
    };
    /* O Esc da gaveta fecharia a gaveta inteira; enquanto a confirmação
       está aberta, ele só fecha a confirmação. */
    const aoCancelarDialog = (e) => e.preventDefault();

    camada.addEventListener('click', (e) => {
      const acao = e.target.closest('[data-acao]');
      if (acao) fechar(acao.dataset.acao === 'esvaziar');
      else if (e.target === camada) fechar(false);
    });
    document.addEventListener('keydown', aoTeclar, true);
    if (host.tagName === 'DIALOG') host.addEventListener('cancel', aoCancelarDialog);

    host.appendChild(camada);
    camada.querySelector('[data-acao="cancelar"]').focus();
  });
}

document.addEventListener('click', async (event) => {
  const botao = event.target.closest('[data-lcw-esvaziar]');
  if (!botao) return;
  event.preventDefault();

  if (!(await confirmar(botao))) return;

  botao.disabled = true;
  try {
    await esvaziar();
  } catch (erro) {
    console.error(erro);
    // Se a atualização em tela falhar, recarrega: o carrinho já foi limpo no servidor.
    window.location.reload();
  } finally {
    botao.disabled = false;
  }
});
